import type { Endomorphism } from '@code-expert/prelude';
import { flow, number, string } from '@code-expert/prelude';
import { locale } from './locale';

export const makeNumberFormat = (options?: Intl.NumberFormatOptions) =>
  new Intl.NumberFormat(locale, options);

export const numberFormat = makeNumberFormat();

/**
 * Rounds percentages to the nearest integer, but takes care to only round to 100% if the
 * value is really close to 100, i.e. would render 99.9 as 99.
 */
export const roundPercent = (percentage: number): number => {
  const isApproximatelyOneHundred = number.getEqAbsolute(1e-6).equals(percentage, 100);
  const rounded = Math.round(percentage);
  // Don't round up to 100% if threshold is not reached.
  return rounded === 100 && !isApproximatelyOneHundred ? 99 : rounded;
};

export const replaceMinus: Endomorphism<string> = string.replace('-', '\u2212');

export interface PercentOptions {
  renderPercent?: Endomorphism<string>;
}

const renderPercentWithOptions =
  ({ renderPercent = string.append('%') }: PercentOptions) =>
  (percentage: number): string =>
    renderPercent(replaceMinus(roundPercent(percentage).toString()));

/**
 * Renders percentages rounded to the nearest integer, but takes care to only round to 100% if the
 * value is really close to 100, i.e. would render 99.9 as 99%.
 */
export const renderPercent = renderPercentWithOptions({});

export const formatPercentWithOptions = (options: PercentOptions): ((n: number) => string) =>
  flow(number.percentFromUnitInterval, renderPercentWithOptions(options));

/**
 * Format a number within the unit interval [0, 1] as a percentage (e.g. 0.56 as "56%").
 * Only displays percentages in the range of [0, 100].
 */
export const formatPercent = formatPercentWithOptions({});
